import { ArrowUpRight, ShieldCheck, Clock, FileClock, Car } from "lucide-react";

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-[#0A0A0A] text-white"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://images.pexels.com/photos/15489246/pexels-photo-15489246.jpeg"
          alt=""
          className="w-full h-full object-cover opacity-70"
        />
      </div>
      {/* Gradient overlays */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/85 to-[#0A0A0A]/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
      <div className="absolute inset-0 grid-pattern opacity-30" />
      <div className="noise-overlay" />

      {/* French flag vertical bars (left edge) */}
      <div className="hidden md:flex absolute left-0 top-0 bottom-0 w-1 flex-col z-10">
        <div className="flex-1 bg-[#0055A4]" />
        <div className="flex-1 bg-white" />
        <div className="flex-1 bg-[#EF4135]" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 pt-32 pb-24 lg:pt-40 lg:pb-32 min-h-screen flex flex-col justify-center">
        <div className="max-w-4xl">
          <div
            data-testid="hero-eyebrow"
            className="animate-fade-up inline-flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-[#007AFF] border border-[#007AFF]/30 bg-[#007AFF]/10 px-4 py-2 mb-10"
          >
            <span className="w-1.5 h-1.5 bg-[#007AFF] rounded-full animate-pulse" />
            Spécialiste international DSP · 5 continents
          </div>

          <h1
            data-testid="hero-title"
            className="animate-fade-up font-heading text-5xl sm:text-6xl lg:text-8xl font-black tracking-[-0.03em] uppercase leading-[0.9] mb-8"
            style={{ animationDelay: "0.1s" }}
          >
            Expert du<br />
            <span className="text-white/40">débosselage</span><br />
            <span className="inline-flex items-baseline gap-4">
              sans peinture
              <span className="inline-block w-4 h-4 bg-[#EF4135]" />
            </span>
          </h1>

          {/* French flag divider (logo-inspired) */}
          <div
            className="animate-fade-up flex items-center gap-3 mb-8"
            style={{ animationDelay: "0.15s" }}
          >
            <div className="h-[3px] w-12 bg-[#0055A4]" />
            <div className="h-[3px] w-12 bg-white" />
            <div className="h-[3px] w-12 bg-[#EF4135]" />
            <span className="text-[10px] uppercase tracking-[0.4em] text-white/50 ml-2">
              Savoir-faire français
            </span>
          </div>

          <p
            data-testid="hero-subtitle"
            className="animate-fade-up max-w-2xl text-base md:text-lg text-white/70 leading-relaxed mb-12"
            style={{ animationDelay: "0.2s" }}
          >
            Réparation de grêle, chocs de parking, petites bosses. De la
            voiture au poids lourd : particuliers, utilitaires, camping-cars,
            camions et semi-remorques. <span className="text-white font-semibold">Spécialistes des carrosseries aluminium et carbone.</span>{" "}
            Partenaires de confiance des compagnies d'assurance et des
            constructeurs automobiles, en France et à l'international.
          </p>

          <div
            className="animate-fade-up flex flex-wrap items-center gap-4 mb-16"
            style={{ animationDelay: "0.3s" }}
          >
            <a
              href="#contact"
              data-testid="hero-cta-devis"
              className="group inline-flex items-center gap-3 bg-[#007AFF] hover:bg-[#3B82F6] text-white text-sm font-bold uppercase tracking-widest px-8 py-4 transition-colors accent-glow"
            >
              Demander un devis
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
            <a
              href="#services"
              data-testid="hero-cta-services"
              className="inline-flex items-center gap-3 border border-white/20 hover:border-white/60 text-white text-sm font-bold uppercase tracking-widest px-8 py-4 transition-colors"
            >
              Voir nos services
            </a>
          </div>

          {/* Quick highlights */}
          <div
            className="animate-fade-up grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10 max-w-3xl"
            style={{ animationDelay: "0.4s" }}
          >
            {[
              { icon: Clock, label: "Intervention sous 48h", value: "Rapidité", color: "#007AFF" },
              { icon: FileClock, label: "Devis sous 24h", value: "Devis", color: "#FFFFFF" },
              { icon: Car, label: "Véhicule de prêt", value: "Mobilité", color: "#007AFF" },
              { icon: ShieldCheck, label: "Garantie préservée", value: "Sécurité", color: "#EF4135" },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-[#0A0A0A] p-5 flex items-start gap-3"
              >
                <item.icon
                  className="w-5 h-5 mt-0.5 flex-shrink-0"
                  style={{ color: item.color }}
                />
                <div>
                  <div className="text-[10px] text-white/50 uppercase tracking-widest">
                    {item.value}
                  </div>
                  <div className="text-xs font-semibold text-white mt-1">
                    {item.label}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Logo showcase in right corner (large screens) */}
        <div className="hidden xl:block absolute right-12 top-32 z-10">
          <div className="relative w-64 h-64 border border-white/10 bg-black/60 backdrop-blur-sm p-6 flex items-center justify-center group hover:border-white/30 transition-colors">
            <img
              src="/assets/logo-pdr-france.jpeg"
              alt="PDR France - Logo"
              className="w-full h-full object-contain"
              data-testid="hero-logo"
            />
            {/* Corner accents */}
            <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-[#0055A4]" />
            <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-white" />
            <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-[#EF4135]" />
            <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-[#0055A4]" />
          </div>
          <div className="mt-4 text-center text-[10px] uppercase tracking-[0.4em] text-white/40">
            — Marque française —
          </div>
        </div>
      </div>
    </section>
  );
};
